import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../SalesBalanceReport/ExpandedTable';
import { FaEye, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { Button, Row, Col, Modal, Table } from 'react-bootstrap';
import axios from 'axios';
import baseURL from '../../../../Url/NodeBaseURL';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FaFileExcel } from "react-icons/fa";

const Categorybyweight = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [categoryDetails, setCategoryDetails] = useState(null);

    // Group data by category
    const groupByCategory = (data) => {
        const grouped = data.reduce((acc, item) => {
            const key = item.category || 'UNCATEGORIZED';
            if (!acc[key]) {
                acc[key] = {
                    category: key,
                    items: [],
                    total_gross_weight: 0,
                    total_net_weight: 0,
                    total_wastage_weight: 0,
                    total_stone_weight: 0,
                    total_items: 0,
                    total_qty: 0,
                };
            }
            acc[key].items.push(item);
            acc[key].total_gross_weight += parseFloat(item.gross_weight) || 0;
            acc[key].total_net_weight += parseFloat(item.weight_bw) || 0;
            acc[key].total_wastage_weight += parseFloat(item.wastage_weight) || 0;
            acc[key].total_stone_weight += parseFloat(item.stone_weight) || 0;
            acc[key].total_items += 1;
            acc[key].total_qty += Number(item.qty) || 0;
            return acc;
        }, {});

        return grouped;
    };

    const handleDownloadExcel = () => {
        if (!groupedData.length) return;

        const excelData = [];

        groupedData.forEach((category) => {
            category.items.forEach((item) => {
                excelData.push({
                    "S No": excelData.length + 1,
                    "Category": category.category,
                    "Product Name": item.product_name || "",
                    "Metal Type": item.metal_type || "",
                    "Purity": item.purity || "",
                    "Gross Weight": item.gross_weight || 0,
                    "Stone Weight": item.stone_weight || 0,
                    "Net Weight": item.weight_bw || 0,
                    "Wastage Weight": item.wastage_weight || 0,
                    "Total Weight AV": item.total_weight_av || 0,
                    "Qty": item.qty || 0,
                    "Invoice No": item.invoice_number || "",
                    "Date": item.date
                        ? new Date(item.date).toLocaleDateString("en-GB")
                        : "",
                });
            });
        });

        // Add category summary rows
        excelData.push({});
        excelData.push({
            "S No": "SUMMARY",
            "Category": "Category",
            "Product Name": "Total Items",
            "Metal Type": "Total Qty",
            "Purity": "Total Gross Weight",
            "Gross Weight": "Total Net Weight",
            "Stone Weight": "Total Wastage Weight",
        });

        groupedData.forEach((category) => {
            excelData.push({
                "S No": "",
                "Category": category.category,
                "Product Name": category.total_items,
                "Metal Type": category.total_qty,
                "Purity": category.total_gross_weight.toFixed(3),
                "Gross Weight": category.total_net_weight.toFixed(3),
                "Stone Weight": category.total_wastage_weight.toFixed(3),
            });
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Category Weight Report");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });

        const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");

        saveAs(blob, `Category_Weight_Report_${yyyy}-${mm}-${dd}.xlsx`);
    };

    const columns = React.useMemo(
        () => [
            {
                Header: '',
                accessor: 'expander',
                Cell: ({ row }) => (
                    <span {...row.getToggleRowExpandedProps()}>
                        {row.isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                    </span>
                ),
            },
            {
                Header: 'Category',
                accessor: 'category',
            },
            {
                Header: 'Total Items',
                accessor: 'total_items',
                Cell: ({ row }) => row.original.items.length,
            },
            {
                Header: 'Total Qty',
                accessor: 'total_qty',
                Cell: ({ row }) => {
                    const total = row.original.items.reduce((sum, item) =>
                        sum + (Number(item.qty) || 0), 0);
                    return total;
                },
            },
            {
                Header: 'Total Gross Weight (g)',
                accessor: 'total_gross_weight',
                Cell: ({ row }) => {
                    const total = row.original.items.reduce((sum, item) =>
                        sum + (parseFloat(item.gross_weight) || 0), 0);
                    return total.toFixed(3);
                },
            },
            {
                Header: 'Total Net Weight (g)',
                accessor: 'total_net_weight',
                Cell: ({ row }) => {
                    const total = row.original.items.reduce((sum, item) =>
                        sum + (parseFloat(item.weight_bw) || 0), 0);
                    return total.toFixed(3);
                },
            },
            {
                Header: 'Total Wastage Weight (g)',
                accessor: 'total_wastage_weight',
                Cell: ({ row }) => {
                    const total = row.original.items.reduce((sum, item) =>
                        sum + (parseFloat(item.wastage_weight) || 0), 0);
                    return total.toFixed(3);
                },
            },
            {
                Header: 'Total Stone Weight (g)',
                accessor: 'total_stone_weight',
                Cell: ({ row }) => {
                    const total = row.original.items.reduce((sum, item) =>
                        sum + (parseFloat(item.stone_weight) || 0), 0);
                    return total.toFixed(3);
                },
            },
        ],
        []
    );

    const itemColumns = React.useMemo(
        () => [
            {
                Header: 'Invoice No.',
                accessor: 'invoice_number',
                Cell: ({ value }) => value || 'N/A'
            },
            {
                Header: 'Date',
                accessor: 'date',
                Cell: ({ value }) => formatDate(value) || 'N/A'
            },
            {
                Header: 'Product Name',
                accessor: 'product_name',
                Cell: ({ value }) => value || 'N/A'
            },
            {
                Header: 'Metal Type',
                accessor: 'metal_type',
                Cell: ({ value }) => value || 'N/A'
            },
            {
                Header: 'Purity',
                accessor: 'purity',
                Cell: ({ value }) => value || 'N/A'
            },
            {
                Header: 'Gross Wt (g)',
                accessor: 'gross_weight',
                Cell: ({ value }) => (value ? parseFloat(value).toFixed(3) : '0.000')
            },
            {
                Header: 'Stone Wt (g)',
                accessor: 'stone_weight',
                Cell: ({ value }) => (value ? parseFloat(value).toFixed(3) : '0.000')
            },
            {
                Header: 'Net Wt (g)',
                accessor: 'weight_bw',
                Cell: ({ value }) => (value ? parseFloat(value).toFixed(3) : '0.000')
            },
            {
                Header: 'Wastage Wt (g)',
                accessor: 'wastage_weight',
                Cell: ({ value }) => (value ? parseFloat(value).toFixed(3) : '0.000')
            },
            {
                Header: 'Total Wt AV (g)',
                accessor: 'total_weight_av',
                Cell: ({ value }) => (value ? parseFloat(value).toFixed(3) : '0.000')
            },
            {
                Header: 'Qty',
                accessor: 'qty',
                Cell: ({ value }) => value || 0
            },
            {
                Header: 'Actions',
                accessor: 'actions',
                Cell: ({ row }) => (
                    <FaEye
                        style={{ cursor: 'pointer', color: 'green' }}
                        onClick={() => handleViewDetails(row.original.invoice_number)}
                    />
                ),
            },
        ],
        []
    );

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return `${String(date.getDate()).padStart(2, '0')}-${String(
            date.getMonth() + 1
        ).padStart(2, '0')}-${date.getFullYear()}`;
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`${baseURL}/get-unique-repair-details`);
                const filteredData = response.data.filter(
                    (item) => item.transaction_status === 'Sales' || item.transaction_status === "ConvertedInvoice"
                );
                setData(filteredData);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching repair details:', error);
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleViewDetails = async (invoice_number) => {
        try {
            const response = await axios.get(`${baseURL}/get-repair-details/${invoice_number}`);
            setCategoryDetails(response.data);
            setShowModal(true);
        } catch (error) {
            console.error('Error fetching repair details:', error);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setCategoryDetails(null);
    };

    const groupedData = React.useMemo(() => {
        const grouped = groupByCategory(data);
        return Object.values(grouped).sort((a, b) =>
            b.total_gross_weight - a.total_gross_weight
        );
    }, [data]);

    return (
        <div className="main-container">
            <div className="payments-table-container">
                <Row className="mb-3">
                    <Col className="d-flex justify-content-between align-items-center">
                        <h3>Category Wise Sold Weight Report</h3>

                        <Button
                            variant="success"
                            onClick={handleDownloadExcel}
                            className="d-flex align-items-center gap-2"
                        >
                            <FaFileExcel />
                            Download Excel
                        </Button>
                    </Col>
                </Row>

                {loading ? (
                    <p>Loading...</p>
                ) : (
                    <DataTable
                        columns={columns}
                        data={groupedData}
                        renderRowSubComponent={({ row }) => (
                            <div style={{ padding: '20px', backgroundColor: '#f8f9fa' }}>
                                <Table striped bordered responsive>
                                    <thead>
                                        <tr>
                                            {itemColumns.map((column, i) => (
                                                <th key={i}>{column.Header}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {row.original.items.map((item, i) => (
                                            <tr key={i}>
                                                {itemColumns.map((column, j) => {
                                                    if (column.accessor) {
                                                        return (
                                                            <td key={j}>
                                                                {column.Cell ?
                                                                    column.Cell({
                                                                        value: item[column.accessor],
                                                                        row: { original: item }
                                                                    }) :
                                                                    item[column.accessor]
                                                                }
                                                            </td>
                                                        );
                                                    }
                                                    if (column.id) {
                                                        return (
                                                            <td key={j}>
                                                                {column.Cell({ row: { original: item } })}
                                                            </td>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                            </tr>
                                        ))}
                                        <tr style={{ fontWeight: 'bold', backgroundColor: '#e9ecef' }}>
                                            <td colSpan="5" className="text-end">
                                                Total ({row.original.items.length} items)
                                            </td>
                                            <td>
                                                {row.original.items.reduce((sum, item) =>
                                                    sum + (parseFloat(item.gross_weight) || 0), 0).toFixed(3)}
                                            </td>
                                            <td>
                                                {row.original.items.reduce((sum, item) =>
                                                    sum + (parseFloat(item.stone_weight) || 0), 0).toFixed(3)}
                                            </td>
                                            <td>
                                                {row.original.items.reduce((sum, item) =>
                                                    sum + (parseFloat(item.weight_bw) || 0), 0).toFixed(3)}
                                            </td>
                                            <td>
                                                {row.original.items.reduce((sum, item) =>
                                                    sum + (parseFloat(item.wastage_weight) || 0), 0).toFixed(3)}
                                            </td>
                                            <td>
                                                {row.original.items.reduce((sum, item) =>
                                                    sum + (parseFloat(item.total_weight_av) || 0), 0).toFixed(3)}
                                            </td>
                                            <td>
                                                {row.original.items.reduce((sum, item) =>
                                                    sum + (Number(item.qty) || 0), 0)}
                                            </td>
                                            <td></td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    />
                )}
            </div>

            <Modal show={showModal} onHide={handleCloseModal} size="xl" className='m-auto'>
                <Modal.Header closeButton>
                    <Modal.Title>Invoice Details</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ fontSize: '13px' }}>
                    {categoryDetails && (
                        <>
                            <h5>Customer Info</h5>
                            <Table bordered>
                                <tbody>
                                    <tr>
                                        <td>Customer ID</td>
                                        <td>{categoryDetails.uniqueData.customer_id}</td>
                                    </tr>
                                    <tr>
                                        <td>Mobile</td>
                                        <td>{categoryDetails.uniqueData.mobile}</td>
                                    </tr>
                                    <tr>
                                        <td>Account Name</td>
                                        <td>{categoryDetails.uniqueData.account_name}</td>
                                    </tr>
                                    <tr>
                                        <td>Email</td>
                                        <td>{categoryDetails.uniqueData.email}</td>
                                    </tr>
                                    <tr>
                                        <td>Address</td>
                                        <td>{categoryDetails.uniqueData.address1}</td>
                                    </tr>
                                    <tr>
                                        <td>Invoice Number</td>
                                        <td>{categoryDetails.uniqueData.invoice_number}</td>
                                    </tr>
                                    <tr>
                                        <td>Total Amount</td>
                                        <td>{categoryDetails.uniqueData.net_amount}</td>
                                    </tr>
                                </tbody>
                            </Table>

                            <h5>Products</h5>
                            <div className="table-responsive">
                                <Table bordered>
                                    <thead style={{ whiteSpace: 'nowrap' }}>
                                        <tr>
                                            <th>BarCode</th>
                                            <th>Product Name</th>
                                            <th>Category</th>
                                            <th>Metal</th>
                                            <th>Purity</th>
                                            <th>Gross Wt</th>
                                            <th>Stone Wt</th>
                                            <th>W.Wt</th>
                                            <th>Total Wt</th>
                                            <th>Qty</th>
                                            <th>Total Price</th>
                                        </tr>
                                    </thead>
                                    <tbody style={{ whiteSpace: 'nowrap' }}>
                                        {categoryDetails.repeatedData.map((product, index) => (
                                            <tr key={index}>
                                                <td>{product.code}</td>
                                                <td>{product.product_name}</td>
                                                <td>{product.category}</td>
                                                <td>{product.metal_type}</td>
                                                <td>{product.purity}</td>
                                                <td>{product.gross_weight}</td>
                                                <td>{product.stone_weight}</td>
                                                <td>{product.wastage_weight}</td>
                                                <td>{product.total_weight_av}</td>
                                                <td>{product.qty}</td>
                                                <td>{product.total_price}</td>
                                            </tr>
                                        ))}
                                        <tr style={{ fontWeight: 'bold' }}>
                                            <td colSpan="10" className="text-end">
                                                Total Amount
                                            </td>
                                            <td>{categoryDetails.uniqueData.net_amount}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Categorybyweight;